import {createAsyncThunk, createSlice, current} from "@reduxjs/toolkit"
import axios from "axios"
import {DYNAMIC_PANEL_TAB_INDEX_MAP, setDynamicView} from "./dynamicViewSlice"
import {breadcrumbDataToNoteIdsAsStrings} from "../utils/noteUtilities"
import { fetchNoteAttachments, fetchSingleNoteAttachments } from "./noteAttachmentsSlice"
import {deleteArticle} from "./deletedArticlesSlice"
import {ensureWorkspaceTemplate} from "../utils/reduxUtilities"
import {EMPTY_ARRAY} from "./reducerUtilities"

export const fetchNoteCitationsAndSwitchPanels = createAsyncThunk(
	"noteArticleTabsV2/fetchNoteCitationsAndSwitchPanels",
	async (payload, {getState, dispatch}) => {
		const {note, singleNote} = payload
		const state = getState()
		const { noteArticleTabsV2 } = state
		const {workspaceId} = note
		const workspace = ensureWorkspace(noteArticleTabsV2, workspaceId)
		const page = 1
		let articles = []
		let count = 0
		// init tab
		const tab = workspace.tabs[note.id]
		let queryString = singleNote ? `/api/workspace/${workspaceId}/note/${note.id}/singleNoteArticles` : `/api/workspace/${workspaceId}/note/${note.id}/article`
		const { data } = await axios.get(queryString, {
			params: { page }
		})
		articles = data.articles
		count = data.count
		// init link attachments
		if (singleNote) {
			dispatch(fetchSingleNoteAttachments({note}))
		} else {
			dispatch(fetchNoteAttachments({note}))
		}
		// update view
		dispatch(setDynamicView({newValue: DYNAMIC_PANEL_TAB_INDEX_MAP.NOTE_ARTICLE_LIST, workspaceId}))
		return {
			note,
			articles,
			count,
			singleNote,
			tab
		}
	}
)

// Make Async Note Article Page
// Fulfilled action should request next page of articles
export const incrementPageAndFetchArticles = createAsyncThunk(
	"noteArticleTabsV2/setArticlePageAsync",
	async ({tab, note, page, singleNote}) => {
		const {workspaceId} = note
		const { data } = await axios.get(`/api/workspace/${workspaceId}/note/${note.id}/article`, {
			params: { page }
		})
		let nextArticles = data.articles
		let count = data.count

		return {
			page,
			tab,
			nextArticles,
			count,
			note,
			singleNote,
		}
	}
)

const initialState = {
	currentIndex: -1,
	tabs: {},
	tabInsertionOrder: [],
	loading: false
}
const ensureWorkspace = (state, workspaceId) => ensureWorkspaceTemplate(state, workspaceId, initialState)

export const noteArticleTabsV2 = createSlice({
	name: "noteArticleTabsV2",
	initialState: {
		byWorkspace: {}
	},
	reducers: {
		setCurrentTabIndex: (state, action) => {
			const {index, workspaceId} = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			workspace.currentIndex = index
		},
		setNoteArticlePage: (state, action) => {
			//TODO: currently unused??
			const {page, noteId, workspaceId} = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			workspace.tabs[noteId] = {
				...workspace.tabs[noteId],
				page,
			}
		},
		handleNoteArticle: (state, action) => {
			const {note, articles, singleNote, page, count: totalArticleCount, fromNoteList} = action.payload
			const {workspaceId} = note
			const workspace = ensureWorkspace(state, workspaceId)
			const noteId = note.id.toString()

			if(!workspace.tabs[noteId]) { //tab doesn't currently exist
				const title = note.content
				const count = articles.length
				const newTab = {
					page,
					hasMore: articles.length < totalArticleCount,
					title,
					articles,
					singleNote,
					meta: {note, count},
				}

				workspace.tabs[noteId] = newTab
				workspace.tabInsertionOrder.push(noteId)
			} else {
				let updatedArticles = []
				// if tab exists but is from noteList or tab change
				if (fromNoteList) {
					updatedArticles = articles
				} else {
					updatedArticles = state.tabs[noteId].articles.concat(articles)
				}
				state.tabs[noteId].articles = updatedArticles
				state.tabs[noteId].singleNote = singleNote
				state.tabs[noteId].hasMore = updatedArticles.length < totalArticleCount
			}

			state.currentIndex = `${note.id}`
			state.loading = false
		},
		updateNoteArticleAsImported: (state, action) => {
			const {article: workspaceArticle, workspaceId} = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			const workspaceStateCache = current(workspace)
			for(let tab of workspaceStateCache.tabInsertionOrder) {
				const affectedTab = workspaceStateCache.tabs[tab]
				const updatedTabArticleList = affectedTab.articles.map(article => {
					if (article.articleId === workspaceArticle.articleId) {
						return {
							...workspaceArticle
						}
					} else {
						return article
					}
				})
				workspace.tabs[tab].articles = updatedTabArticleList
			}
		},
		removeTab: (state, action) => {
			const {tabIndex, workspaceId} = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			const deletingNoteIndex = workspace.tabInsertionOrder.findIndex(tabId => tabId === tabIndex)
			const noteTabIsSelected = workspace.currentIndex === tabIndex
			const isOnlyNote = workspace.tabInsertionOrder.length === 1

			delete workspace.tabs[action.payload] // delete object
			workspace.tabInsertionOrder.splice(deletingNoteIndex, 1) // delete index reference

			if(noteTabIsSelected && !isOnlyNote) { // move index if a new one is needed
				deletingNoteIndex > 0 ? workspace.currentIndex = workspace.tabInsertionOrder[deletingNoteIndex - 1] : workspace.currentIndex = workspace.tabInsertionOrder[0]
			} else if (noteTabIsSelected && isOnlyNote) { // if selected note was index 0, reset the slice
				workspace.currentIndex = -1
			}
		},
		clearTabs: (state, action) => {
			// TODO: this is currently unused
			const workspace = ensureWorkspace(state, action.payload)
			workspace.currentIndex = -1
			workspace.tabs = {}
			workspace.loading = false
			workspace.tabInsertionOrder = []
		},
		pushAttachedArticlesToNoteTab: (state, action) => {
			const {noteId, articleWorkspaces} = action.payload
			const {workspaceId} = articleWorkspaces[0]

			// if the workspace doesn't exist, the tab we're checking for also doesn't exist...
			if (!state.byWorkspace[workspaceId]) return

			const workspace = state.byWorkspace[workspaceId]
			const stateCache = current(workspace)
			const affectedTab = stateCache.tabs[noteId]

			if(affectedTab) {
				articleWorkspaces.forEach(articleWorkspace => {
					// this is needed when an article is deleted than reimported and attached to the same note
					if(affectedTab.articles.find(article => article.articleId === articleWorkspace.articleId)){
						return
					} else {
						workspace.tabs[noteId].articles.push(articleWorkspace)
					}
				})
			}

		},
	},
	extraReducers: (builder) => {
		builder.addCase(
			fetchNoteCitationsAndSwitchPanels.pending,
			(state, action) => {
				const {workspaceId} = action.meta.arg.note
				const workspace = ensureWorkspace(state, workspaceId)
				workspace.loading = true
			}
		)
		builder.addCase(
			fetchNoteCitationsAndSwitchPanels.rejected,
			(state, action) => {
				const {workspaceId} = action.meta.arg.note
				const workspace = ensureWorkspace(state, workspaceId)
				workspace.loading = false
			}
		)
		builder.addCase(
			fetchNoteCitationsAndSwitchPanels.fulfilled,
			(state, action) => { 
				const {
					note,
					articles,
					count,
					singleNote,
				} = action.payload
				const workspace = ensureWorkspace(state, note.workspaceId)

				const title = note.content
				const newTab = {
					page: 1,
					hasMore: articles.length < count,
					title,
					articles,
					singleNote,
					meta: {note, count},
				}
				workspace.currentIndex = note.id
				workspace.loading = false
				workspace.tabs[note.id] = newTab
				// Check if note.id already exists in tabInsertionOrder before pushing
				if (!workspace.tabInsertionOrder.includes(note.id)) {
					workspace.tabInsertionOrder.push(note.id)
				}
			}
		)
		builder.addCase(
			incrementPageAndFetchArticles.pending,
			(state, action) => {
				const {workspaceId} = action.meta.arg.note
				const workspace = ensureWorkspace(state, workspaceId)
				workspace.loading = true
			}
		)
		builder.addCase(
			incrementPageAndFetchArticles.rejected,
			(state, action) => {
				const {workspaceId} = action.meta.arg.note
				const workspace = ensureWorkspace(state, workspaceId)
				workspace.loading = false
			}
		)
		builder.addCase(
			incrementPageAndFetchArticles.fulfilled,
			(state, action) => {
				const {
					page,
					tab,
					nextArticles,
					count,
					note,
					singleNote
				} = action.payload
				const {workspaceId} = note
				const workspace = ensureWorkspace(state, workspaceId)

				// set new page state
				// concat nextArticles with tab articles
				// update count???
				let articles = [...tab.articles, ...nextArticles]
				workspace.tabs[note.id] = {
					...tab,
					page,
					articles,
					hasMore: articles.length < count,
					meta: {note, count},
					title: note.content,
					singleNote
				}
				workspace.loading = false
			}
		)
		builder.addCase(
			deleteArticle.fulfilled,
			(state, action) => {
				const {workspaceId, article} = action.payload
				const {articleId: deletedArticleId, path, deletedAt} = article
				if(!state.byWorkspace[workspaceId]) {
					return
				} else {
					let affectedIds = []
					const workspace = ensureWorkspace(state, workspaceId)
					path.forEach(idPath => {
						const stringToArray = breadcrumbDataToNoteIdsAsStrings(idPath)
						affectedIds = affectedIds.concat(stringToArray)
					})
					const stateCache = current(workspace)
					if(stateCache.tabInsertionOrder !== initialState.tabInsertionOrder){
						for(let tab of stateCache.tabInsertionOrder) {
							const affectedTab = stateCache.tabs[tab]
							const updatedTabArticleList = affectedTab.articles.map(article => {
								if (article.articleId === deletedArticleId) {
									return {
										...article,
										deletedAt,
									}
								} else {
									return article
								}
							})
							workspace.tabs[tab].articles = updatedTabArticleList
						}
					}
				}
			}
		)
	}
})

export const {
	setCurrentTabIndex,
	removeTab,
	pushAttachedArticlesToNoteTab,
	updateNoteArticleAsImported,
} = noteArticleTabsV2.actions

export const selectNoteArticleTabsListV2 = (state, workspaceId) => {
	const tabs = state.noteArticleTabsV2.byWorkspace[workspaceId]?.tabs
	return tabs && Object.keys(tabs).length > 0 ? tabs : false
}

// // Add page selector using conditionals like selectNoteArticleTabsArticles and selectNoteArticleSelectedTab
// export const selectCurrentNoteArticleTab = (state) => state.noteArticleTabsV2.tabs[state.noteArticleTabsV2.currentIndex]
// export const selectNoteArticleTabPage = (state) => state.noteArticleTabsV2.tabs[state.noteArticleTabsV2.currentIndex] && state.noteArticleTabsV2.tabs[state.noteArticleTabsV2.currentIndex].page
// export const selectNoteArticleTabHasMore = (state) => state.noteArticleTabsV2.tabs[state.noteArticleTabsV2.currentIndex] && state.noteArticleTabsV2.tabs[state.noteArticleTabsV2.currentIndex].hasMore
// export const selectNoteArticleTabsArticles = (state) => state.noteArticleTabsV2.tabs[state.noteArticleTabsV2.currentIndex] ? state.noteArticleTabsV2.tabs[state.noteArticleTabsV2.currentIndex].articles : []
// export const selectNoteArticleSelectedTab = (state) => state.noteArticleTabsV2.tabs[state.noteArticleTabsV2.currentIndex] ? state.noteArticleTabsV2.tabs[state.noteArticleTabsV2.currentIndex] : {}
// export const selectNoteArticleTabsCurrentIndexV2 = (state) => state.noteArticleTabsV2.currentIndex
// export const selectNoteArticleTabsIsLoading = (state) => state.noteArticleTabsV2.loading
// export const selectNoteArticleTabsInsertionOrder = (state) => state.noteArticleTabsV2.tabInsertionOrder

const getWorkspace = (state, workspaceId) =>
	state.noteArticleTabsV2.byWorkspace[workspaceId]

const getCurrentTab = (state, workspaceId) => {
	const ws = getWorkspace(state, workspaceId)
	return ws?.tabs?.[ws?.currentIndex]
}

export const selectCurrentNoteArticleTab = (state, workspaceId) =>
	getCurrentTab(state, workspaceId) ?? null

export const selectNoteArticleTabPage = (state, workspaceId) =>
	getCurrentTab(state, workspaceId)?.page ?? null

export const selectNoteArticleTabHasMore = (state, workspaceId) =>
	getCurrentTab(state, workspaceId)?.hasMore ?? false

export const selectNoteArticleTabsArticles = (state, workspaceId) =>
	getCurrentTab(state, workspaceId)?.articles ?? EMPTY_ARRAY

export const selectNoteArticleSelectedTab = (state, workspaceId) =>
	getCurrentTab(state, workspaceId) ?? initialState.tabs

export const selectNoteArticleTabsCurrentIndexV2 = (state, workspaceId) =>
	getWorkspace(state, workspaceId)?.currentIndex ?? initialState.currentIndex

export const selectNoteArticleTabsIsLoading = (state, workspaceId) =>
	getWorkspace(state, workspaceId)?.loading ?? initialState.loading

export const selectNoteArticleTabsInsertionOrder = (state, workspaceId) =>
	getWorkspace(state, workspaceId)?.tabInsertionOrder ?? initialState.tabInsertionOrder

export default noteArticleTabsV2.reducer
